import { Card, CardContent, TextField } from '@mui/material'
import AutoScroll from 'components/common/Enhancers/AutoScroll'
import TableButton from 'components/common/Buttons/TableButton'
import Table from 'components/common/Table'
import { calculateDayTimeAvg } from 'helpers/dayTime'
import useQuery from 'hooks/useQuery'
import React, { useContext, useMemo, useState } from 'react'
import { AdminDataContext } from 'context/AdminDataContext'

const CLIENT_COLUMNS_TO_INCLUDE = ['companyName', 'speciality']
const Stats = () => {
  const { clients } = useContext(AdminDataContext)

  const [userId, setUserId] = useState(null)
  const aditionalColumns = [
    {
      Header: 'Estadísticas',
      Cell: ({
        row: {
          original: { id },
        },
        cellCustomData,
      }) => (
        <TableButton
          selected={id === cellCustomData}
          clickHandler={() => setUserId((ps) => (ps === id ? null : id))}
        >
          Ver Estadísticas
        </TableButton>
      ),
    },
  ]
  return (
    <>
      <div className='w-full'>
        {/* rendering the same component with a ternary operator
         is virtually the same as changing its props, a different key props is used to avoid it */}
        <h1>Estadísticas</h1>
        <Table
          endpoint='/admins/clients'
          tableData={clients}
          aditionalColumns={aditionalColumns}
          columnsToInclude={CLIENT_COLUMNS_TO_INCLUDE}
          cellCustomData={userId}
          disableAutoScroll
          loaderWidth='50%'
        />
      </div>
      {userId && <ClientCard userId={userId} />}
    </>
  )
}

const ClientCard = ({ userId }) => {
  const { data, fallbackComponent } = useQuery({
    defaultEndpoint: `/admins/clients/${userId}/stats`,
    loaderWidth: '50%',
    loaderHeight: '50vh',
  })

  const totalStats = useMemo(
    () =>
      (!data
        ? {}
        : {
          earliestCall: data.Accounts.length
            ? data.Accounts.reduce((p, c) =>
              (c.earliestCall < p.earliestCall ? c : p)).earliestCall
            : '-',
          latestCall: data.Accounts.length
            ? data.Accounts.reduce((p, c) =>
              (c.latestCall > p.latestCall ? c : p)).latestCall
            : '-',
          callDurationAvg: (
            data.Accounts.reduce((p, c) => p + c.callDurationAvg, 0)
              / (data.Accounts.length || 1)
          ).toFixed(2),
          callsCount: data.Accounts.reduce((p, c) => p + c.callsCount, 0),
          totalCallsDuration: data.Accounts.reduce(
            (p, c) => p + c.totalCallsDuration,
            0,
          ),
          callsDayMomentAvg:
              calculateDayTimeAvg(
                data.Accounts.map((a) => a.callsDayMomentAvg),
              ) || '-',
          mostRecentCall: data.mostRecentCall,
        }),
    [data],
  )

  if (fallbackComponent) return fallbackComponent

  return (
    <AutoScroll dependencies={data} scrollMargin='10vh'>
      <Card
        className='w-full sm:w-max max-w-full m-2 rounded-lg my-4'
        elevation={2}
      >
        <CardContent>
          <div className='flex justify-between items-center gap-4 my-2'>
            <h1>{data.companyName || `${data.firstName} ${data.lastName}`}</h1>
            <span className='text-right'>
              {`Mediador: ${data.mediatorName || '-b'}`}
            </span>
          </div>
          <CardContent>
            <h2 className='text-center sm:text-left'>Estadísticas globales</h2>
            {data.Accounts.length ? (
              <CallUserCard data={totalStats} />
            ) : (
              <span>No posee llamadas registradas</span>
            )}
          </CardContent>
          {!!data.Accounts.length && (
            <div className='flex flex-col gap-4 pt-4 border-t-2'>
              <h2>Estadísticas por Cuenta</h2>
              {data.Accounts.map((a) => {
                return (
                  <div className='flex flex-col' key={a.id}>
                    <h3 className='text-center sm:text-left'>{a.name}</h3>
                    {a.callsCount ? (
                      <CallUserCard data={a} />
                    ) : (
                      <span className='text-[var(--color-light-black)]'>
                        No posee llamadas registradas
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </AutoScroll>
  )
}

const CallUserCard = ({ data }) => {
  return (
    <div
      key={data.id}
      className='flex flex-col sm:items-center sm:grid grid-cols-2 gap-2'
    >
      <DataDisplayItem label='Llamada más temprana' value={data.earliestCall} />
      <DataDisplayItem label='Llamada más tardía' value={data.latestCall} />
      <DataDisplayItem
        label='Promedio duración de llamada'
        value={`${data.callDurationAvg} hs`}
      />
      <DataDisplayItem
        label='Hora promedio de inicio de llamada'
        value={data.callsDayMomentAvg}
      />
      <DataDisplayItem label='Total de llamadas' value={data.callsCount} />
      <DataDisplayItem
        label='Duración total de llamadas'
        value={`${data.totalCallsDuration} hs`}
      />
      <DataDisplayItem label='Última llamada' value={data.mostRecentCall} />
    </div>
  )
}

const DataDisplayItem = ({ label, value }) => {
  return (
    <TextField
      variant='filled'
      label={label}
      value={value}
      aria-readonly
      readOnly
      style={{ pointerEvents: 'none', minWidth: '220px' }}
    />
  )
}

export default Stats
